import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { calendarService } from '../../services/calendarService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { validateAppointmentSchedule } from '../../utils/appointmentConfig';
import { useAppointmentAlert } from '../../contexts/AppointmentAlertContext';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const EVENT_TYPE_COLORS = {
  work_order: 'bg-blue-500',
  followup: 'bg-yellow-500',
  delivery: 'bg-green-500',
  inspection: 'bg-purple-500',
};

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  rescheduled: 'bg-yellow-100 text-yellow-700',
};

// ─── Utilidad de sonido ───────────────────────────────────────────────────────
const playAlertSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playBeep = (freq, startTime, duration) => {
      const osc = ctx?.createOscillator();
      const gain = ctx?.createGain();
      osc?.connect(gain);
      gain?.connect(ctx?.destination);
      osc.type = 'sine';
      osc?.frequency?.setValueAtTime(freq, startTime);
      gain?.gain?.setValueAtTime(0.3, startTime);
      gain?.gain?.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc?.start(startTime);
      osc?.stop(startTime + duration);
    };
    const t = ctx?.currentTime;
    playBeep(880, t, 0.15);
    playBeep(1100, t + 0.18, 0.15);
    playBeep(880, t + 0.36, 0.2);
  } catch (_) { /* silencioso si el navegador bloquea */ }
};

// ─── Solicitar permiso de notificaciones ──────────────────────────────────────
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'denied';
  }
};

// ─── Enviar notificación del sistema ──────────────────────────────────────────
const sendSystemNotification = (title, body) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: '/favicon.ico' });
  } catch (_) { /* silencioso */ }
};

// ─── Calcular mensaje de tiempo restante ─────────────────────────────────────
const getTimeRemainingMessage = (startDatetime) => {
  const now = new Date();
  const start = new Date(startDatetime);
  const diffMs = start - now;
  if (diffMs <= 0) return 'La cita ya comenzó';
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 60) return `Tu cita es en ${diffMin} minuto${diffMin !== 1 ? 's' : ''}`;
  const diffHrs = Math.floor(diffMin / 60);
  const remMin = diffMin % 60;
  if (remMin === 0) return `Tu cita es en ${diffHrs} hora${diffHrs !== 1 ? 's' : ''}`;
  return `Tu cita es en ${diffHrs}h ${remMin}min`;
};

// ─── Barra de Alerta Superior ─────────────────────────────────────────────────
const EventAlertBar = ({ alerts, onVerEvento, onPosponer, onAtendido, onClose, notifPermission }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const [blink, setBlink] = useState(true);
  const prevAlertIdsRef = useRef('');

  // Reset índice y reproducir sonido cuando cambia la lista de alertas
  useEffect(() => {
    if (!alerts?.length) return;
    const newIds = alerts?.map(a => a?.id)?.join(',');
    if (newIds !== prevAlertIdsRef?.current) {
      setCurrentIdx(0);
      setVisible(true);
      prevAlertIdsRef.current = newIds;
      // Reproducir sonido siempre que llegue una alerta nueva
      playAlertSound();
      // Enviar notificación del sistema para citas con recordatorio
      const citaAlert = alerts?.find(a => a?.isCitaReminder);
      if (citaAlert) {
        const clienteInfo = citaAlert?.clienteNombre ? `Cliente: ${citaAlert?.clienteNombre}` : '';
        const vehiculoInfo = citaAlert?.vehiculoInfo ? ` | ${citaAlert?.vehiculoInfo}` : '';
        const timeMsg = getTimeRemainingMessage(citaAlert?.startDatetime);
        sendSystemNotification('Cita próxima', `${timeMsg}${clienteInfo ? '\n' + clienteInfo : ''}${vehiculoInfo}`);
      }
    }
  }, [alerts]);

  // Parpadeo suave
  useEffect(() => {
    const interval = setInterval(() => setBlink(b => !b), 900);
    return () => clearInterval(interval);
  }, []);

  // Ciclar entre alertas si hay más de una
  useEffect(() => {
    if (alerts?.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIdx(i => (i + 1) % alerts?.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [alerts]);

  if (!visible || !alerts?.length) return null;

  const alert = alerts?.[currentIdx];
  const isOverdue = alert?.isOverdue;
  const isCitaReminder = alert?.isCitaReminder;

  const bgClass = isOverdue ? 'bg-red-600' : isCitaReminder ? 'bg-blue-600' : 'bg-amber-500';
  const blinkClass = blink ? 'opacity-100' : 'opacity-80';

  const formatTime = (dt) => {
    if (!dt) return '—';
    return new Date(dt)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dt) => {
    if (!dt) return '—';
    return new Date(dt)?.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const responsible = alert?.assignedUser?.fullName || alert?.assignedUser?.full_name || alert?.responsableNombre || 'Sin asignar';

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] transition-opacity duration-500 ${blinkClass} ${bgClass} shadow-lg`}
      role="alert"
      aria-live="assertive"
    >
      <div className="max-w-full px-3 py-2 md:py-2.5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          {/* Ícono + etiqueta */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
              <Icon name={isOverdue ? 'AlertTriangle' : isCitaReminder ? 'Calendar' : 'Bell'} size={16} className="text-white" />
            </span>
            <span className="text-white font-bold text-xs uppercase tracking-wide hidden sm:inline">
              {isOverdue ? 'Actividad Vencida' : isCitaReminder ? 'Cita próxima' : 'Próxima Actividad'}
            </span>
            {alert?.sourceType === 'crm' && (
              <span className="text-white/80 text-xs bg-white/20 px-1.5 py-0.5 rounded hidden sm:inline">CRM</span>
            )}
            {alerts?.length > 1 && (
              <span className="text-white/70 text-xs">({currentIdx + 1}/{alerts?.length})</span>
            )}
          </div>

          {/* Info del evento */}
          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <p className="text-white font-semibold text-sm truncate">{alert?.title}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-white/90 text-xs">
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={12} />
                {formatDate(alert?.startDatetime)} · {formatTime(alert?.startDatetime)}
              </span>
              {isCitaReminder && alert?.clienteNombre && (
                <span className="flex items-center gap-1">
                  <Icon name="User" size={12} />
                  {alert?.clienteNombre}
                </span>
              )}
              {isCitaReminder && alert?.vehiculoInfo && (
                <span className="flex items-center gap-1">
                  <Icon name="Car" size={12} />
                  {alert?.vehiculoInfo}
                </span>
              )}
              {!isCitaReminder && (
                <span className="flex items-center gap-1">
                  <Icon name="User" size={12} />
                  {responsible}
                </span>
              )}
              <span className="flex items-center gap-1 font-medium text-white">
                <Icon name="Bell" size={12} />
                {getTimeRemainingMessage(alert?.startDatetime)}
              </span>
            </div>
          </div>

          {/* Aviso de permisos desactivados */}
          {notifPermission === 'denied' && (
            <div className="flex items-center gap-1 text-white/90 text-xs bg-white/20 px-2 py-1 rounded flex-shrink-0">
              <Icon name="BellOff" size={12} />
              <span className="hidden sm:inline">Activa notificaciones en tu navegador</span>
            </div>
          )}

          {/* Acciones */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => onVerEvento(alert)}
              className="px-2.5 py-1 rounded-md bg-white text-gray-800 text-xs font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Ver evento
            </button>
            <button
              onClick={() => onPosponer(alert)}
              className="px-2.5 py-1 rounded-md bg-white/20 text-white text-xs font-semibold hover:bg-white/30 transition-colors whitespace-nowrap border border-white/30"
            >
              Posponer
            </button>
            <button
              onClick={() => onAtendido(alert)}
              className="px-2.5 py-1 rounded-md bg-white/20 text-white text-xs font-semibold hover:bg-white/30 transition-colors whitespace-nowrap border border-white/30"
            >
              Atendido ✓
            </button>
            <button
              onClick={() => { setVisible(false); onClose?.(); }}
              className="ml-1 p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Cerrar alerta"
            >
              <Icon name="X" size={14} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Modal Posponer ───────────────────────────────────────────────────────────
const PosponerModal = ({ event, onConfirm, onCancel }) => {
  const [minutes, setMinutes] = useState(15);
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg border border-border shadow-elevation-5 p-6 w-full max-w-sm">
        <h3 className="text-base font-semibold text-foreground mb-1">Posponer evento</h3>
        <p className="text-sm text-muted-foreground mb-4 truncate">{event?.title}</p>
        <label className="block text-sm font-medium text-foreground mb-2">¿Cuántos minutos posponer?</label>
        <div className="flex gap-2 mb-4">
          {[15, 30, 60]?.map(m => (
            <button
              key={m}
              onClick={() => setMinutes(m)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                minutes === m
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-foreground hover:bg-muted'
              }`}
            >
              {m} min
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={onCancel}>Cancelar</Button>
          <Button variant="default" fullWidth onClick={() => onConfirm(event, minutes)}>Posponer</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const OperationalCalendar = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { showAppointmentAlert } = useAppointmentAlert();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [events, setEvents] = useState([]);
  const [crmActivities, setCrmActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Estado de alertas
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [dismissedAlertIds, setDismissedAlertIds] = useState(new Set());
  const [posponerEvent, setPosponerEvent] = useState(null);
  const [alertBarVisible, setAlertBarVisible] = useState(true);
  const alertCheckRef = useRef(null);
  const prevAlertIdsRef = useRef('');
  const [notifPermission, setNotifPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    relatedType: 'followup',
    startDatetime: '',
    endDatetime: '',
    status: 'scheduled',
    color: '#3B82F6'
  });

  const getMonthRange = useCallback((date) => {
    const start = new Date(date?.getFullYear(), date?.getMonth(), 1);
    const end = new Date(date?.getFullYear(), date?.getMonth() + 1, 0, 23, 59, 59);
    return { start: start?.toISOString(), end: end?.toISOString() };
  }, []);

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    try {
      const { start, end } = getMonthRange(currentDate);
      const data = await calendarService?.getEvents(start, end);
      setEvents(data);
    } catch (err) {
      console.error('Error loading calendar events:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate, getMonthRange]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ── Solicitar permiso de notificaciones al montar ──────────────────────────
  useEffect(() => {
    const askPermission = async () => {
      const result = await requestNotificationPermission();
      setNotifPermission(result);
    };
    askPermission();
  }, []);

  // ── Cargar actividades CRM pendientes ──────────────────────────────────────
  const loadCrmActivities = useCallback(async () => {
    const companyId = userProfile?.company_id;
    const userId = user?.id;
    if (!companyId || !userId) return;
    try {
      const { data, error } = await supabase
        ?.from('crm_activities')
        ?.select('*, contact:crm_contacts(id, nombre, telefono), reminder_minutes')
        ?.eq('company_id', companyId)
        ?.eq('estado', 'pendiente')
        ?.order('fecha', { ascending: true })
        ?.order('hora', { ascending: true });
      if (error) { console.error('Error loading CRM activities:', error); return; }
      // Normalizar a formato compatible con alertas del calendario
      const normalized = (data || [])?.map(a => {
        const fechaHora = a?.fecha && a?.hora
          ? `${a?.fecha}T${a?.hora}`
          : a?.fecha
            ? `${a?.fecha}T09:00:00`
            : null;
        return {
          id: `crm_${a?.id}`,
          _crmId: a?.id,
          title: a?.titulo || a?.tipo || 'Actividad CRM',
          description: a?.descripcion || '',
          startDatetime: fechaHora,
          status: 'scheduled',
          sourceType: 'crm',
          responsableNombre: a?.contact?.nombre || a?.responsable || 'Sin asignar',
          assignedUser: { fullName: a?.contact?.nombre || a?.responsable || 'Sin asignar' },
          reminder_minutes: a?.reminder_minutes ?? null,
          rawActivity: a,
        };
      })?.filter(a => a?.startDatetime);
      setCrmActivities(normalized);
    } catch (err) {
      console.error('loadCrmActivities error:', err);
    }
  }, [user?.id, userProfile?.company_id]);

  useEffect(() => { loadCrmActivities(); }, [loadCrmActivities]);

  // ── Lógica de alertas ──────────────────────────────────────────────────────
  const computeAlerts = useCallback((calEvts, crmEvts, dismissed) => {
    const now = new Date();
    const CALENDAR_WINDOW_MS = 15 * 60 * 1000; // 15 min fijo para calendar_events sin reminder

    const filterCalendar = (e) => {
      if (dismissed?.has(e?.id)) return false;
      if (!e?.startDatetime) return false;
      if (e?.status !== 'scheduled') return false;
      const start = new Date(e?.startDatetime);
      if (isNaN(start?.getTime())) return false;
      const diff = start - now;
      const isOverdue = diff < 0;
      // Si tiene reminder_minutes configurado, usar esa ventana
      if (e?.reminderMinutes !== null && e?.reminderMinutes !== undefined && e?.reminderMinutes > 0) {
        const reminderMs = e?.reminderMinutes * 60 * 1000;
        const isUpcoming = diff >= 0 && diff <= reminderMs;
        return isUpcoming || isOverdue;
      }
      // Sin reminder_minutes: ventana fija de 15 min
      const isUpcoming = diff >= 0 && diff <= CALENDAR_WINDOW_MS;
      return isUpcoming || isOverdue;
    };

    const filterCrm = (e) => {
      if (dismissed?.has(e?.id)) return false;
      if (!e?.startDatetime) return false;
      if (e?.status !== 'scheduled') return false;
      // Si reminder_minutes es null o 0, no hay alerta para esta actividad
      if (e?.reminder_minutes === null || e?.reminder_minutes === undefined || e?.reminder_minutes === 0) return false;
      const start = new Date(e?.startDatetime);
      if (isNaN(start?.getTime())) return false;
      const diff = start - now;
      const reminderMs = e?.reminder_minutes * 60 * 1000;
      const isUpcoming = diff >= 0 && diff <= reminderMs;
      const isOverdue = diff < 0;
      return isUpcoming || isOverdue;
    };

    const mapCalendar = (e) => {
      // Extraer info de cliente y vehículo de la descripción si existe
      const desc = e?.description || '';
      const clienteMatch = desc?.match(/Cliente:\s*([^|]+)/);
      const vehiculoMatch = desc?.match(/\|\s*(.+)/);
      const clienteNombre = e?.client?.nombre || (clienteMatch ? clienteMatch?.[1]?.trim() : null);
      const vehiculoInfo = e?.vehicle
        ? `${e?.vehicle?.marca || ''} ${e?.vehicle?.modelo || ''} ${e?.vehicle?.year || ''}`?.trim()
        : (vehiculoMatch ? vehiculoMatch?.[1]?.trim() : null);
      return {
        ...e,
        isOverdue: new Date(e?.startDatetime) < now,
        isCitaReminder: !!(e?.reminderMinutes !== null && e?.reminderMinutes !== undefined && e?.reminderMinutes > 0),
        clienteNombre,
        vehiculoInfo,
      };
    };

    const mapFn = (e) => ({
      ...e,
      isOverdue: new Date(e?.startDatetime) < now,
    });

    const calAlerts = (calEvts || [])?.filter(filterCalendar)?.map(mapCalendar);
    const crmAlerts = (crmEvts || [])?.filter(filterCrm)?.map(mapFn);

    // Ordenar: vencidos primero, luego por hora
    return [...calAlerts, ...crmAlerts]?.sort((a, b) => {
      if (a?.isOverdue && !b?.isOverdue) return -1;
      if (!a?.isOverdue && b?.isOverdue) return 1;
      return new Date(a?.startDatetime) - new Date(b?.startDatetime);
    });
  }, []);

  useEffect(() => {
    const check = () => {
      const alerts = computeAlerts(events, crmActivities, dismissedAlertIds);
      setActiveAlerts(prev => {
        const prevIds = prev?.map(a => a?.id)?.join(',');
        const newIds = alerts?.map(a => a?.id)?.join(',');
        if (prevIds !== newIds) {
          // Si hay nuevas alertas, siempre mostrar la barra (incluso si fue cerrada)
          if (alerts?.length > 0) {
            setAlertBarVisible(true);
          }
          return alerts;
        }
        return prev;
      });
    };
    check();
    // Revisar cada 30 segundos para mayor reactividad
    alertCheckRef.current = setInterval(check, 30000);
    return () => clearInterval(alertCheckRef?.current);
  }, [events, crmActivities, dismissedAlertIds, computeAlerts]);

  const handleVerEvento = (alert) => {
    setSelectedEvent(alert);
  };

  const handlePosponer = (alert) => {
    setPosponerEvent(alert);
  };

  const handleConfirmPosponer = async (alert, minutes) => {
    try {
      if (alert?.sourceType === 'crm' && alert?._crmId) {
        // Posponer actividad CRM: actualizar fecha/hora
        const newStart = new Date(new Date(alert?.startDatetime).getTime() + minutes * 60000);
        const newFecha = newStart?.toISOString()?.split('T')?.[0];
        const newHora = newStart?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
        await supabase?.from('crm_activities')?.update({ fecha: newFecha, hora: newHora })?.eq('id', alert?._crmId);
        await loadCrmActivities();
      } else {
        const newStart = new Date(new Date(alert?.startDatetime).getTime() + minutes * 60000);
        await calendarService?.updateEvent(alert?.id, {
          startDatetime: newStart?.toISOString(),
          status: 'rescheduled',
        });
        await loadEvents();
      }
      setDismissedAlertIds(prev => new Set([...prev, alert?.id]));
      setPosponerEvent(null);
    } catch (err) {
      console.error('Error al posponer evento:', err);
      setPosponerEvent(null);
    }
  };

  const handleAtendido = async (alert) => {
    try {
      if (alert?.sourceType === 'crm' && alert?._crmId) {
        await supabase?.from('crm_activities')?.update({ estado: 'completada' })?.eq('id', alert?._crmId);
        await loadCrmActivities();
      } else {
        await calendarService?.updateEvent(alert?.id, { status: 'completed' });
        await loadEvents();
      }
      setDismissedAlertIds(prev => new Set([...prev, alert?.id]));
    } catch (err) {
      console.error('Error al marcar atendido:', err);
    }
  };

  const handleSaveEvent = async () => {
    if (!newEvent?.title?.trim()) { setSaveError('El título es obligatorio'); return; }
    if (!newEvent?.startDatetime) { setSaveError('La fecha de inicio es obligatoria'); return; }

    // ── Validación de horario laboral ──────────────────────────────────────────
    const datePart = newEvent?.startDatetime?.split('T')?.[0];
    const timePart = newEvent?.startDatetime?.split('T')?.[1]?.slice(0, 5);
    if (datePart && timePart) {
      const scheduleCheck = validateAppointmentSchedule(datePart, timePart);
      if (!scheduleCheck?.valid) { setSaveError(scheduleCheck?.error); return; }
    }

    setSaveError('');
    setIsSaving(true);
    try {
      await calendarService?.createEvent(newEvent);

      // ── Alerta visual de cita ──────────────────────────────────────────────
      showAppointmentAlert({
        tipo: 'nueva',
        origen: 'Calendario',
        cliente: newEvent?.description || null,
        telefono: null,
        vehiculo: null,
        placa: null,
        servicio: newEvent?.title,
        fecha: datePart || null,
        hora: timePart || null,
        notas: null,
        usuario: userProfile?.full_name || userProfile?.email || 'Sistema',
      });

      setShowNewEventModal(false);
      setNewEvent({ title: '', description: '', relatedType: 'followup', startDatetime: '', endDatetime: '', status: 'scheduled', color: '#3B82F6' });
      await loadEvents();
    } catch (err) {
      setSaveError(err?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateEventStatus = async (eventId, status) => {
    try {
      await calendarService?.updateEvent(eventId, { status });
      setSelectedEvent(null);
      await loadEvents();
    } catch (err) {
      console.error('Error updating event:', err);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1)?.getDay();
    const daysInMonth = new Date(year, month + 1, 0)?.getDate();
    return { firstDay, daysInMonth };
  };

  const getEventsForDay = (day) => {
    return events?.filter(event => {
      const eventDate = new Date(event?.startDatetime);
      return eventDate?.getDate() === day &&
        eventDate?.getMonth() === currentDate?.getMonth() &&
        eventDate?.getFullYear() === currentDate?.getFullYear();
    });
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    const day = start?.getDay();
    start?.setDate(start?.getDate() - day);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d?.setDate(start?.getDate() + i);
      return d;
    });
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const today = new Date();

  const prevMonth = () => setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() + 1, 1));
  const prevWeek = () => { const d = new Date(currentDate); d?.setDate(d?.getDate() - 7); setCurrentDate(d); };
  const nextWeek = () => { const d = new Date(currentDate); d?.setDate(d?.getDate() + 7); setCurrentDate(d); };

  const upcomingEvents = events
    ?.filter(e => new Date(e?.startDatetime) >= today && e?.status === 'scheduled')
    ?.sort((a, b) => new Date(a?.startDatetime) - new Date(b?.startDatetime))
    ?.slice(0, 5);

  const alertBarHeight = activeAlerts?.length > 0 && alertBarVisible ? 'pt-14 sm:pt-12' : '';

  return (
    <div className="min-h-screen bg-background">
      {/* ── Barra de Alerta Superior ── */}
      {activeAlerts?.length > 0 && alertBarVisible && (
        <EventAlertBar
          alerts={activeAlerts}
          onVerEvento={handleVerEvento}
          onPosponer={handlePosponer}
          onAtendido={handleAtendido}
          onClose={() => setAlertBarVisible(false)}
          notifPermission={notifPermission}
        />
      )}
      <MainSidebar isCollapsed={isSidebarCollapsed} />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ${alertBarHeight}`}>
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0">
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="text-xl font-heading font-semibold text-foreground leading-tight">Calendario Operativo</h1>
                <p className="text-sm text-muted-foreground mt-1">Agenda de órdenes, entregas y seguimientos</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {activeAlerts?.length > 0 && !alertBarVisible && (
                <button
                  onClick={() => setAlertBarVisible(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-semibold border border-amber-300 hover:bg-amber-200 transition-colors"
                >
                  <Icon name="Bell" size={14} />
                  {activeAlerts?.length} alerta{activeAlerts?.length > 1 ? 's' : ''}
                </button>
              )}
              <div className="flex rounded-lg border border-border overflow-hidden">
                {['month', 'week', 'agenda']?.map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      viewMode === mode ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {mode === 'month' ? 'Mes' : mode === 'week' ? 'Semana' : 'Agenda'}
                  </button>
                ))}
              </div>
              <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowNewEventModal(true)}>Nuevo Evento</Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Calendar */}
            <div className="lg:col-span-3">
              <div className="bg-card rounded-lg border border-border">
                {/* Navigation */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <button onClick={viewMode === 'week' ? prevWeek : prevMonth} className="p-2 rounded-lg hover:bg-muted">
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  <h2 className="text-lg font-semibold text-foreground">
                    {viewMode === 'week'
                      ? `Semana del ${getWeekDays()?.[0]?.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
                      : `${MONTHS?.[currentDate?.getMonth()]} ${currentDate?.getFullYear()}`
                    }
                  </h2>
                  <button onClick={viewMode === 'week' ? nextWeek : nextMonth} className="p-2 rounded-lg hover:bg-muted">
                    <Icon name="ChevronRight" size={20} />
                  </button>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : viewMode === 'month' ? (
                  <div>
                    <div className="grid grid-cols-7 border-b border-border">
                      {DAYS?.map(day => (
                        <div key={day} className="py-2 text-center text-xs font-medium text-muted-foreground">{day}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7">
                      {Array.from({ length: firstDay })?.map((_, i) => (
                        <div key={`empty-${i}`} className="h-24 border-b border-r border-border" />
                      ))}
                      {Array.from({ length: daysInMonth }, (_, i) => i + 1)?.map(day => {
                        const dayEvents = getEventsForDay(day);
                        const isToday = day === today?.getDate() && currentDate?.getMonth() === today?.getMonth() && currentDate?.getFullYear() === today?.getFullYear();
                        return (
                          <div key={day} className={`h-24 border-b border-r border-border p-1 overflow-hidden ${
                            isToday ? 'bg-primary/5' : 'hover:bg-muted/30'
                          }`}>
                            <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${
                              isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                            }`}>{day}</span>
                            {dayEvents?.slice(0, 2)?.map(event => (
                              <button
                                key={event?.id}
                                onClick={() => setSelectedEvent(event)}
                                className={`w-full text-left text-xs px-1 py-0.5 rounded mt-0.5 text-white truncate ${EVENT_TYPE_COLORS?.[event?.relatedType] || 'bg-gray-500'}`}
                              >
                                {event?.title}
                              </button>
                            ))}
                            {dayEvents?.length > 2 && (
                              <p className="text-xs text-muted-foreground mt-0.5">+{dayEvents?.length - 2} más</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : viewMode === 'week' ? (
                  <div>
                    <div className="grid grid-cols-7 border-b border-border">
                      {getWeekDays()?.map((day, i) => {
                        const isToday = day?.toDateString() === today?.toDateString();
                        const dayEvts = events?.filter(e => new Date(e?.startDatetime)?.toDateString() === day?.toDateString());
                        return (
                          <div key={i} className={`p-3 border-r border-border min-h-32 ${isToday ? 'bg-primary/5' : ''}`}>
                            <div className="text-center mb-2">
                              <p className="text-xs text-muted-foreground">{DAYS?.[day?.getDay()]}</p>
                              <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full mx-auto ${
                                isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                              }`}>{day?.getDate()}</span>
                            </div>
                            {dayEvts?.map(event => (
                              <button
                                key={event?.id}
                                onClick={() => setSelectedEvent(event)}
                                className={`w-full text-left text-xs px-1.5 py-1 rounded mb-1 text-white ${EVENT_TYPE_COLORS?.[event?.relatedType] || 'bg-gray-500'}`}
                              >
                                <p className="truncate font-medium">{event?.title}</p>
                                <p className="truncate opacity-80">{new Date(event?.startDatetime)?.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {events?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No hay eventos este mes</p>
                    ) : events?.sort((a, b) => new Date(a?.startDatetime) - new Date(b?.startDatetime))?.map(event => (
                      <button
                        key={event?.id}
                        onClick={() => setSelectedEvent(event)}
                        className="w-full flex items-start gap-4 p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/30 transition-colors text-left"
                      >
                        <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-1.5 ${EVENT_TYPE_COLORS?.[event?.relatedType] || 'bg-gray-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground">{event?.title}</p>
                          {event?.description && <p className="text-sm text-muted-foreground truncate">{event?.description}</p>}
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(event?.startDatetime)?.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS?.[event?.status] || 'bg-gray-100 text-gray-700'}`}>
                          {event?.status === 'scheduled' ? 'Programado' : event?.status === 'completed' ? 'Completado' : event?.status === 'cancelled' ? 'Cancelado' : 'Reprogramado'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar: Upcoming Events */}
            <div className="space-y-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="CalendarDays" size={18} className="text-primary" />
                  Próximos Eventos
                </h3>
                {upcomingEvents?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay eventos próximos</p>
                ) : upcomingEvents?.map(event => (
                  <button
                    key={event?.id}
                    onClick={() => setSelectedEvent(event)}
                    className="w-full flex items-start gap-3 py-3 border-b border-border last:border-0 text-left hover:bg-muted/30 rounded-lg px-2 transition-colors"
                  >
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${EVENT_TYPE_COLORS?.[event?.relatedType] || 'bg-gray-500'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{event?.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(event?.startDatetime)?.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Legend */}
              <div className="bg-card rounded-lg border border-border p-4">
                <h3 className="font-semibold text-foreground mb-3">Leyenda</h3>
                <div className="space-y-2">
                  {[
                    { type: 'work_order', label: 'Orden de Trabajo' },
                    { type: 'followup', label: 'Seguimiento' },
                    { type: 'delivery', label: 'Entrega' },
                    { type: 'inspection', label: 'Inspección' },
                  ]?.map(item => (
                    <div key={item?.type} className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${EVENT_TYPE_COLORS?.[item?.type]}`} />
                      <span className="text-sm text-muted-foreground">{item?.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* ── Modal Posponer ── */}
      {posponerEvent && (
        <PosponerModal
          event={posponerEvent}
          onConfirm={handleConfirmPosponer}
          onCancel={() => setPosponerEvent(null)}
        />
      )}
      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg border border-border shadow-elevation-5 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{selectedEvent?.title}</h3>
              <button onClick={() => setSelectedEvent(null)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {selectedEvent?.description && <p className="text-muted-foreground">{selectedEvent?.description}</p>}
              <p className="text-foreground">
                <span className="font-medium">Inicio:</span> {new Date(selectedEvent?.startDatetime)?.toLocaleString('es-MX')}
              </p>
              {selectedEvent?.endDatetime && (
                <p className="text-foreground">
                  <span className="font-medium">Fin:</span> {new Date(selectedEvent?.endDatetime)?.toLocaleString('es-MX')}
                </p>
              )}
              {selectedEvent?.assignedUser && (
                <p className="text-foreground">
                  <span className="font-medium">Responsable:</span> {selectedEvent?.assignedUser?.fullName || selectedEvent?.assignedUser?.full_name || 'Sin asignar'}
                </p>
              )}
              {selectedEvent?.client && <p className="text-foreground"><span className="font-medium">Cliente:</span> {selectedEvent?.client?.nombre}</p>}
              {selectedEvent?.vehicle && <p className="text-foreground"><span className="font-medium">Vehículo:</span> {selectedEvent?.vehicle?.marca} {selectedEvent?.vehicle?.modelo}</p>}
              <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS?.[selectedEvent?.status] || 'bg-gray-100 text-gray-700'}`}>
                {selectedEvent?.status === 'scheduled' ? 'Programado' : selectedEvent?.status === 'completed' ? 'Completado' : selectedEvent?.status === 'cancelled' ? 'Cancelado' : 'Reprogramado'}
              </span>
            </div>
            <div className="flex gap-2 mt-5">
              {selectedEvent?.status === 'scheduled' && (
                <>
                  <Button variant="default" size="sm" fullWidth onClick={() => handleUpdateEventStatus(selectedEvent?.id, 'completed')}>Completar</Button>
                  <Button variant="outline" size="sm" fullWidth onClick={() => handleUpdateEventStatus(selectedEvent?.id, 'cancelled')}>Cancelar</Button>
                </>
              )}
              {selectedEvent?.status !== 'scheduled' && (
                <Button variant="outline" fullWidth onClick={() => setSelectedEvent(null)}>Cerrar</Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* New Event Modal */}
      {showNewEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg border border-border shadow-elevation-5 p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-foreground">Nuevo Evento</h3>
              <button onClick={() => setShowNewEventModal(false)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Título <span className="text-destructive">*</span></label>
                <input type="text" value={newEvent?.title} onChange={(e) => setNewEvent(prev => ({ ...prev, title: e?.target?.value }))} placeholder="Ej: Entrega OT-123456" className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
                <select value={newEvent?.relatedType} onChange={(e) => setNewEvent(prev => ({ ...prev, relatedType: e?.target?.value }))} className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="work_order">Orden de Trabajo</option>
                  <option value="followup">Seguimiento</option>
                  <option value="delivery">Entrega</option>
                  <option value="inspection">Inspección</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
                <textarea value={newEvent?.description} onChange={(e) => setNewEvent(prev => ({ ...prev, description: e?.target?.value }))} rows={2} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Inicio <span className="text-destructive">*</span></label>
                  <input type="datetime-local" value={newEvent?.startDatetime} onChange={(e) => setNewEvent(prev => ({ ...prev, startDatetime: e?.target?.value }))} className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Fin</label>
                  <input type="datetime-local" value={newEvent?.endDatetime} onChange={(e) => setNewEvent(prev => ({ ...prev, endDatetime: e?.target?.value }))} className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              {saveError && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg p-3">
                  <Icon name="AlertCircle" size={16} />
                  {saveError}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" fullWidth onClick={() => setShowNewEventModal(false)}>Cancelar</Button>
                <Button variant="default" fullWidth loading={isSaving} onClick={handleSaveEvent}>Guardar Evento</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationalCalendar;
